import React, { useEffect, useState } from 'react';
import { Grid, Button, Box, useTheme, CircularProgress, Typography, TextField, IconButton, InputAdornment } from '@mui/material';
import { CustomPaper } from '../../styles';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { genotypingSamplersInventoryColumns } from './helpers/GenotypingSamplerInventoryColumns';
import { FaPen, FaTimes } from 'react-icons/fa';
import { request } from '../../../../interfaces/networkManager';
import FormSamplerPopup from './FormSamplerPopup';
import Error from '../../../../components/Error/Error';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Sampler } from '../../../../models/sampler';
import { PaginatedResult } from '../../../../models/paginatedResult';

const requestOptions = {
	method: 'GET',
};

const SamplersPart = () => {
	const [error, setError] = useState<string | null>(null);
	const [datas, setDatas] = useState<PaginatedResult<Sampler>>({pages: [0,0], results: [], total: 0});
	const [datasSearch, setDatasSearch] = useState<PaginatedResult<Sampler>>({pages: [0,0], results: [], total: 0});
	const { spacing } = useTheme();
	const [rowPopup, setRowPopup] = useState<Sampler | null>(null);
	const [search ,setSearch] = useState({value : '', error: false});

	const inputPropsMemo = React.useMemo(
		() => ({
			endAdornment: 
		<InputAdornment position="end">
			<IconButton
				aria-label="toggle password visibility"
				onClick={() => setSearch({ value: '', error: false})}
				edge="end"
				size="small"
			>
				<FaTimes />
			</IconButton>
		</InputAdornment>,
		}),
		[]
	);

	const getActions = (params: { row: Sampler }) => [
		<GridActionsCellItem
			key="answer"
			icon={<FaPen />}
			label="Modifier un préleveur"
			onClick={() => setRowPopup(params.row)}
		/>,
	];

	const columns: GridColDef[] = [
			...genotypingSamplersInventoryColumns,
			{
				headerName: 'Action',
				field: 'actions',
				type: 'actions',
				flex: 1,
				getActions,
			} as GridColDef,
	];

	const reloadDatas = () => {
		request<PaginatedResult<Sampler>>('/phmanager/genotyping/samplers/', requestOptions)
		.then((response: PaginatedResult<Sampler>) => {
			if (!response.results.length){
				return setError('Erreur lors de la récupération des préleveurs');
			}else{
				setDatas(response);
				setDatasSearch(response);
			}
		})
		.catch(() => {
			setError('Erreur lors de la récupération des préleveurs');
		});
	}

	useEffect(() => {
		if(search.value.length !== 0){
			const filteredDatas : Sampler[] = [];
			filteredDatas.length = 0;
			datasSearch.results.forEach((sampler : Sampler) => {
				if(sampler.displayName.toLowerCase().includes(search.value.toLowerCase())||
					sampler.samplerNumber.toLowerCase().includes(search.value.toLowerCase())||
					sampler.organisation.toLowerCase().includes(search.value.toLowerCase()) ||
					sampler.countSample.toString().toLowerCase().includes(search.value.toLowerCase())){
					filteredDatas.push(sampler);
				}
			});
			setDatas({pages: [0, filteredDatas.length], results: filteredDatas, total: filteredDatas.length});
		}else{
			setDatas(datasSearch);
		}
	}, [search, datasSearch]);

	useEffect(() => {
		reloadDatas();
	}, []);
	
	return (
		<>
			<CustomPaper>
				<Grid container mb={1} spacing={1}>
						<Grid size={{xs:12, sm:6, md: 9, lg: 10}}>
							<Typography variant="h5" color="primary" gutterBottom align="left">
								Inventaires des Préleveurs
							</Typography>
						</Grid>
						<Grid size={{xs:12, sm:6, md: 3, lg: 2}}>
							<TextField
								placeholder="Recherche Globale"
								name="search"
								label="Recherche Globale"
								error={search.error}
								value={search.value}
								title="Recherche Globale"
								onChange={(e) => setSearch({value: e.target.value, error: false})}
								InputProps={inputPropsMemo}
							/>
						</Grid>
					</Grid>
					{error && <Error message={error} />}
					{!!datas.total ?(
						<DataGrid 
							getRowId={(row) => row.id}
							rows={datas.results}
							columns={columns}
							initialState={{pagination: {paginationModel: {pageSize: 25}}}}
						/>
					): !error && !search.value ? (
						<Box justifyContent="center" alignItems="center" display="flex" width="100%" minHeight="25rem">
							<CircularProgress />
						</Box>
					) : (
						<Error message={"Aucune données"}/>
					)}
					<Box display="flex" justifyContent="flex-end" mt={spacing(1)}>
						<Button
							sx={{ minWidth: '15rem' }}
							onClick={() => setRowPopup({id: 0, organisation: '', displayName: '', samplerNumber: '', countSample: 0, typePerson: ''})}
							aria-label="Ajouter un préleveur"
							disabled={!!error}
						>
							Ajouter un préleveur
						</Button>
					</Box>
					<ToastContainer />
			</CustomPaper>
			{rowPopup ? (
				<FormSamplerPopup
					sampler={rowPopup}
					isOpen={true}
					onClose={() => setRowPopup(null)}
					reloadDatas={() => reloadDatas()}
					cancelWithTimesIcon={false}
					buttonCancel={true}
					buttonValidate={true}
					labelConfirm={null}
					disabledButtonValidate={false}
					disabledButtonCancel={false}
					labelCancel={null}
				/>
			) : null}
		</>
	);
};

export default SamplersPart;
